import {GraphQLQuery} from '@aws-amplify/api';
import {generateClient} from 'aws-amplify/api';

import {
    getApplicationInfrastructure,
    listApplicationInfrastructure,
} from './infrastructure-queries';
import {
    createApplicationInfrastructure,
    updateApplicationInfrastructure,
} from './infrastructure-mutations';

import {
    ApplicationInfrastructure,
    ApplicationInfrastructureStageInput,
    CreateApplicationInfrastructureInput,
    RegisterInfrastructureFormValues,
    RegisterInfrastructureStageValues,
    UpdateApplicationInfrastructureInput,
} from '../types-infrastructure';

export type ListApplicationInfrastructure = {
    listApplicationInfrastructure: {
        items: ApplicationInfrastructure[];
        nextToken: string | null;
    };
};

export type GetApplicationInfrastructure = {
    getApplicationInfrastructure: ApplicationInfrastructure;
};

export type CreateApplicationInfrastructure = {
    createApplicationInfrastructure: ApplicationInfrastructure;
};

export type UpdateApplicationInfrastructure = {
    updateApplicationInfrastructure: ApplicationInfrastructure;
};

const client = generateClient();

export const fetchRecords = async (
    applicationId: string,
    limit?: number,
    nextToken?: string | null,
) => {
    const response = await client.graphql<GraphQLQuery<ListApplicationInfrastructure>>({
        query: listApplicationInfrastructure,
        variables: {
            applicationId,
            limit,
            nextToken,
        },
    });
    return {
        items: response.data?.listApplicationInfrastructure?.items ?? [],
        nextToken: response.data?.listApplicationInfrastructure?.nextToken ?? null,
    };
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetApplicationInfrastructure>>({
        query: getApplicationInfrastructure,
        variables: {id},
    });
    return response.data?.getApplicationInfrastructure;
};

// Drop a value that the form models as an empty string.
//
// ⚠️ THIS IS THE NO-PADDING RULE. The form always holds a string; the wire must
// never carry `''` for an optional field. An empty string is a VALUE — it reads
// back as "this resource is named the empty string", which is precisely the
// ambiguity the live `infraStackNames = {dev:"", test:"", prod:"…"}` data
// demonstrates and this entity exists to remove.
const omitBlank = (value: string | undefined | null): string | undefined => {
    const trimmed = (value ?? '').trim();
    return trimmed === '' ? undefined : trimmed;
};

// Convert one form stage row into wire shape, or DROP it entirely.
//
// A row is dropped when it cannot satisfy the schema's required quartet
// (`stage`, `accountId`, `region`, `infraStackName`). Sending a partial stage
// is worse than sending none: the schema's own comment is explicit that "a
// stage entry with no stack name is not a stage entry; omit the stage instead",
// because `ValidateDeprovisionable` refuses a missing stack name while a stage
// row that merely LOOKS present passes the eye test.
export const toStageInput = (
    row: RegisterInfrastructureStageValues,
): ApplicationInfrastructureStageInput | undefined => {

    const stage = omitBlank(row.stage);
    const accountId = omitBlank(row.accountId);
    const region = omitBlank(row.region);
    const infraStackName = omitBlank(row.infraStackName);

    if (!stage || !accountId || !region || !infraStackName) {
        return undefined;
    }

    const input: ApplicationInfrastructureStageInput = {
        stage,
        accountId,
        region,
        infraStackName,
    };

    // Optional fields are ADDED ONLY WHEN PRESENT — never assigned `''` and
    // never assigned `null`. An absent key means "not recorded"; an empty
    // string would mean "recorded as blank".
    const deploymentBucketName = omitBlank(row.deploymentBucketName);
    if (deploymentBucketName) {
        input.deploymentBucketName = deploymentBucketName;
    }
    const domain = omitBlank(row.domain);
    if (domain) {
        input.domain = domain;
    }
    const hostedZoneId = omitBlank(row.hostedZoneId);
    if (hostedZoneId) {
        input.hostedZoneId = hostedZoneId;
    }
    const certificateArn = omitBlank(row.certificateArn);
    if (certificateArn) {
        input.certificateArn = certificateArn;
    }

    return input;
};

// The SPARSE array. Incomplete rows vanish rather than padding the array —
// an absent stage means "not provisioned", which is the whole point of the
// array shape over a {dev,test,prod} map.
export const toStageInputs = (
    rows: RegisterInfrastructureStageValues[],
): ApplicationInfrastructureStageInput[] =>
    rows
        .map(toStageInput)
        .filter((row): row is ApplicationInfrastructureStageInput => typeof row !== 'undefined');

// Build the create input for a REGISTRATION.
//
// ⚠️ `origin: REGISTERED` IS NOT OPTIONAL HERE. The API defaults `origin` to
// ORCHESTRATOR when it is omitted, so a registration that fails to send it is
// recorded as though this platform provisioned the resources — which also
// flips `protected` to false and arms a teardown of a live production system.
//
// ⚠️ `protected` IS DELIBERATELY NOT SENT. The resolver derives it from
// `origin`, yielding `true` for REGISTERED. Sending it from a form is how a
// protected record gets accidentally unprotected.
export const buildRegisterInput = (
    applicationId: string,
    values: RegisterInfrastructureFormValues,
): CreateApplicationInfrastructureInput => {

    const input: CreateApplicationInfrastructureInput = {
        applicationId,
        origin: 'REGISTERED' as CreateApplicationInfrastructureInput['origin'],
        stages: toStageInputs(values.stages ?? []),
    };

    const sourceRepositoryName = omitBlank(values.sourceRepositoryName);
    if (sourceRepositoryName) {
        input.sourceRepositoryName = sourceRepositoryName;
    }
    const infraRepositoryName = omitBlank(values.infraRepositoryName);
    if (infraRepositoryName) {
        input.infraRepositoryName = infraRepositoryName;
    }
    const sourcePipelineId = omitBlank(values.sourcePipelineId);
    if (sourcePipelineId) {
        input.sourcePipelineId = sourcePipelineId;
    }
    const infraPipelineId = omitBlank(values.infraPipelineId);
    if (infraPipelineId) {
        input.infraPipelineId = infraPipelineId;
    }
    const deploymentAccountGroupId = omitBlank(values.deploymentAccountGroupId);
    if (deploymentAccountGroupId) {
        input.deploymentAccountGroupId = deploymentAccountGroupId;
    }

    return input;
};

export const add = async (input: CreateApplicationInfrastructureInput) => {
    const response = await client.graphql<GraphQLQuery<CreateApplicationInfrastructure>>({
        query: createApplicationInfrastructure,
        variables: {input},
    });
    return response.data?.createApplicationInfrastructure;
};

// ⚠️ `stages` REPLACES the array wholesale. Any caller amending stages must
// pass the COMPLETE array — read, amend, write. Passing only the changed stage
// deletes every other stage's teardown target.
export const update = async (input: UpdateApplicationInfrastructureInput) => {
    const response = await client.graphql<GraphQLQuery<UpdateApplicationInfrastructure>>({
        query: updateApplicationInfrastructure,
        variables: {input},
    });
    return response.data?.updateApplicationInfrastructure;
};
